import { Controller } from "@hotwired/stimulus";
import { User as SimpleUser } from "./telephony/simple_user";
import { useAudio } from "./telephony/use_audio";
import { useSipUser } from "./telephony/use_sip_user";
import { SessionState } from "sip.js";
import { checkAndRequestMicPermission } from "./telephony/check_permissions";

// Connects to data-controller="dialer"
export default class extends Controller {
  static values = {
    user: String,
    password: String,
    domain: String,
    host: String,
    ringInbound: String,
    ringOutbound: String,
  };

  static targets = [
    "ringer",
    "input",
    "waveform",
    "incomingCalleeName",
    "duration",
    "calleeName",
    "holdButton",
    "muteButton",
    "silenceButton",
    "hangupButton",
    "controls",
    "ringerTemplate",
    "callContainer",
    "controlsTemplate",
    "dialingTemplate",
    "connected",
    "connecting",
    "disconnected",
  ];

  validOptions() {
    if (!this.hasUserValue || this.userValue == "") {
      return false;
    }

    if (!this.hasPasswordValue || this.passwordValue == "") {
      return false;
    }

    if (!this.hasDomainValue || this.domainValue == "") {
      return false;
    }

    return true;
  }

  async connect() {
    await checkAndRequestMicPermission();

    if (!this.validOptions()) {
      return;
    }

    const server = `wss://${this.hostValue}:7443`;

    const config = useSipUser(
      this.userValue,
      this.passwordValue,
      this.domainValue,
    );

    this.audio = useAudio();

    const options = {
      aor: config.aor,
      media: {
        remote: {
          audio: this.audio,
        },
      },
      userAgentOptions: { ...config },
    };

    if (window.registry && window.registry.user) {
      this.user = window.registry.user;

      this.configureDelagate();

      if (window.registry.user.isConnected()) {
        this.connectingTarget.classList.add("hidden");
        this.connectedTarget.classList.remove("hidden");
      } else {
        this.connectingTarget.classList.remove("hidden");
        this.connectedTarget.classList.add("hidden");
        this.disconnectedTarget.classList.add("hidden");
        try {
          await window.registry.user.connect();
          await window.registry.user.register();
          this.connectingTarget.classList.add("hidden");
          this.connectedTarget.classList.remove("hidden");
        } catch (error) {
          this.connectingTarget.classList.add("hidden");
          this.disconnectedTarget.classList.remove("hidden");
        }
      }
    } else {
      window.registry = window.registry || {
        user: new SimpleUser(server, options),
        remoteStream: null,
      };

      this.configureDelagate();

      try {
        window.registry.user.connect().then(() => {
          this.connectingTarget.classList.add("hidden");
          window.registry.user
            .register()
            .then(() => {
              this.connectingTarget.classList.add("hidden");
              this.connectedTarget.classList.remove("hidden");
            })
            .catch((error) => {
              this.connectingTarget.classList.add("hidden");
              this.disconnectedTarget.classList.remove("hidden");
            });
        });
      } catch (error) {
        this.connectingTarget.classList.add("hidden");
        this.disconnectedTarget.classList.remove("hidden");
      }

      this.user = window.registry.user;
    }

    this.interval = setInterval(() => {
      if (!window.registry.user.isConnected()) {
        window.registry = window.registry || {
          user: new SimpleUser(server, options),
          remoteStream: null,
        };

        this.configureDelagate();

        try {
          window.registry.user.connect().then(() => {
            this.connectingTarget.classList.add("hidden");
            window.registry.user
              .register()
              .then(() => {
                this.connectingTarget.classList.add("hidden");
                this.connectedTarget.classList.remove("hidden");
              })
              .catch((error) => {
                this.connectingTarget.classList.add("hidden");
                this.disconnectedTarget.classList.remove("hidden");
              });
          });
        } catch (error) {
          this.connectingTarget.classList.add("hidden");
          this.disconnectedTarget.classList.remove("hidden");
        }

        this.user = window.registry.user;
      }
    }, 3000);
  }

  disconnect() {
    clearInterval(this.interval);
  }

  connectSip() {}

  configureDelagate() {
    this.ringInboudAudio = new Audio(this.ringInboundValue);
    this.ringOutboundAudio = new Audio(this.ringOutboundValue);
    this.ringInboudAudio.loop = true;
    this.ringOutboundAudio.preload = "auto";
    this.ringOutboundAudio.preload = "auto";
    this.ringOutboundAudio.loop = true;

    window.registry.user.delegate = {
      onCallReceived: async (invite) => {
        this.ring(invite);
      },
      onCallAnswered: async (invite) => {
        this.startCall(invite);
        this.ringInboudAudio.pause();
        this.ringOutboundAudio.pause();
      },
      onCallHangup: () => {
        this.stopCallTimer();
        this.callContainerTarget.innerHTML = "";

        this.ringInboudAudio.pause();
        this.ringOutboundAudio.pause();
        this.dispatch("hangup");
      },
      onCallCreated: (session) => {
        // This fires when the INVITE is created — session is now available
        session.stateChange.addListener((state) => {
          console.log("Call state:", state);

          if (state === SessionState.Establishing) {
            // Start ringback when remote is ringing
            this.ringOutboundAudio.play();

            this.ringOutboundAudio.volume = 0.5;
          }

          if (
            state === SessionState.Established ||
            state === SessionState.Terminated
          ) {
            // Stop ringback on answer or failure
            this.ringOutboundAudio.pause();
          }
        });
      },
      onServerDisconnect: () => {
        this.connectingTarget.classList.add("hidden");
        this.connectedTarget.classList.add("hidden");
        this.disconnectedTarget.classList.remove("hidden");
      },

      onUnregistered: () => {
        this.connectingTarget.classList.add("hidden");
        this.connectedTarget.classList.add("hidden");
        this.disconnectedTarget.classList.remove("hidden");
      },
      onRegistered: () => {
        this.connectingTarget.classList.add("hidden");
        this.disconnectedTarget.classList.add("hidden");
        this.connectedTarget.classList.remove("hidden");
      },
    };
  }

  ring(invite) {
    let callerId = this.convertToCallerId(
      invite.remoteIdentity.uri,
      invite.remoteIdentity.displayName,
    );
    let template = this.ringerTemplateTarget.innerHTML.replace(
      "CALLER_ID",
      callerId,
    );
    this.ringInboudAudio.play();
    this.callContainerTarget.innerHTML = template;
  }

  startCall(invite) {
    let callerId = this.convertToCallerId(
      invite.remoteIdentity.uri,
      invite.remoteIdentity.displayName,
    );
    let template = this.controlsTemplateTarget.innerHTML.replace(
      "CALLER_ID",
      callerId,
    );
    this.callContainerTarget.innerHTML = template;
    this.startCallTimer(Date.now());
  }

  convertToCallerId(uri, displayName) {
    if (typeof uri == "string") {
      return (
        uri.replace("sip:", "").split("@")[0] +
        (displayName ? ` (${displayName})` : "")
      );
    } else {
      return uri.normal.user + (displayName ? ` (${displayName})` : "");
    }
  }

  startCallTimer(start) {
    this.callTimerInterval = setInterval(() => {
      const durationMs = Date.now() - start;
      const duration = this.formatDuration(durationMs);
      this.durationTarget.textContent = duration;
    }, 1000);
  }

  stopCallTimer() {
    clearInterval(this.callTimerInterval);
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = "00:00";
    }
  }

  accept() {
    this.ringerTarget.classList.add("hidden");
    window.registry.user.answer();
  }

  reject() {
    this.ringerTarget.classList.add("hidden");
    window.registry.user.decline();
  }

  hold() {
    let holdClasses = "bg-gray-500 text-gray-200".split(" ");
    if (window.registry.user.isHeld()) {
      window.registry.user.unhold();
      this.holdButtonTarget.classList.remove(...holdClasses);
    } else {
      window.registry.user.hold();
      this.holdButtonTarget.classList.add(...holdClasses);
    }
  }

  mute() {
    let muteClasses = "bg-gray-500 text-gray-200".split(" ");
    if (window.registry.user.isMuted()) {
      window.registry.user.unmute();
      this.muteButtonTarget.classList.remove(...muteClasses);
    } else {
      window.registry.user.mute();
      this.muteButtonTarget.classList.add(...muteClasses);
    }
  }

  silence() {
    let silenceClasses = "bg-gray-500 text-gray-200".split(" ");
    if (this.isSilenced) {
      this.audio.muted = false;
      this.isSilenced = false;
      this.silenceButtonTarget.classList.remove(...silenceClasses);
    } else {
      this.audio.muted = true;
      this.isSilenced = true;
      this.silenceButtonTarget.classList.add(...silenceClasses);
    }
  }

  hangup() {
    window.registry.user.hangup();
  }

  formatDuration(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  }

  appendNumber({ params }) {
    const { value } = params;
    this.inputTarget.value += value;
    this.inputTarget.focus();
  }

  backspace() {
    this.inputTarget.value = this.inputTarget.value.slice(0, -1);
    this.inputTarget.focus();
  }

  appendPlus(e) {
    if (e.params.value == "0") {
      e.preventDefault();
      e.stopPropagation();
      this.inputTarget.value += "+";
      this.inputTarget.focus();
    }
  }

  validate({ data }) {
    if (isNaN(data) && !["#", "*", "+"].includes(data)) {
      this.inputTarget.value = this.inputTarget.value.slice(0, -1);
      this.inputTarget.focus();
    }
  }

  async dial({ detail }) {
    const destination = this.buildDestination(detail.value);

    window.registry.user.call(destination);

    let callerId = this.convertToCallerId(destination);
    let template = this.dialingTemplateTarget.innerHTML.replace(
      "CALLER_ID",
      callerId,
    );

    this.callContainerTarget.innerHTML = template;
  }

  buildDestination(number = undefined) {
    return `sip:${number}@${this.domainValue}`;
  }
}
